import { ref } from 'vue'
import type { Ref } from 'vue'
import type { Edition, StoreEditionPayload, UpdateEditionPayload } from '@/types/events'
import {
  listEditions as apiListEditions,
  createEdition as apiCreateEdition,
  getEdition as apiGetEdition,
  updateEdition as apiUpdateEdition,
  activateEdition as apiActivateEdition,
  deleteEdition as apiDeleteEdition,
} from '@/api/events'

const editions = ref<Edition[]>([])
const edition = ref<Edition | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

// Identifies the latest fetchEditions call, so a slower earlier response never overwrites it.
let editionsRequestId = 0

async function fetchEditions(eventSlug: string): Promise<void> {
  const requestId = ++editionsRequestId
  loading.value = true
  error.value = null
  // The list is shared module state: never expose another event's editions while loading or after a failure.
  editions.value = []
  try {
    const fetched = await apiListEditions(eventSlug)
    if (requestId === editionsRequestId) {
      editions.value = fetched
    }
  } catch {
    if (requestId === editionsRequestId) {
      editions.value = []
      error.value = 'common.error_generic'
    }
  } finally {
    if (requestId === editionsRequestId) {
      loading.value = false
    }
  }
}

async function fetchEdition(eventSlug: string, editionSlug: string): Promise<void> {
  loading.value = true
  error.value = null
  try {
    edition.value = await apiGetEdition(eventSlug, editionSlug)
  } catch {
    error.value = 'common.error_generic'
  } finally {
    loading.value = false
  }
}

async function createEdition(
  eventSlug: string,
  payload: StoreEditionPayload,
): Promise<Edition> {
  const created = await apiCreateEdition(eventSlug, payload)
  editions.value = [...editions.value, created]
  return created
}

async function updateEdition(
  eventSlug: string,
  editionSlug: string,
  payload: UpdateEditionPayload,
): Promise<Edition> {
  const updated = await apiUpdateEdition(eventSlug, editionSlug, payload)
  edition.value = updated
  editions.value = editions.value.map((ed) => (ed.slug === editionSlug ? updated : ed))
  return updated
}

async function activateEdition(eventSlug: string, editionSlug: string): Promise<Edition> {
  const updated = await apiActivateEdition(eventSlug, editionSlug)
  editions.value = editions.value.map((ed) => ({
    ...ed,
    active: ed.slug === editionSlug,
  }))
  if (edition.value !== null) {
    edition.value = { ...edition.value, active: edition.value.slug === editionSlug }
  }
  return updated
}

async function deleteEdition(eventSlug: string, editionSlug: string): Promise<void> {
  await apiDeleteEdition(eventSlug, editionSlug)
  editions.value = editions.value.filter((ed) => ed.slug !== editionSlug)
}

export function useEditions(): {
  editions: Ref<Edition[]>
  edition: Ref<Edition | null>
  loading: Ref<boolean>
  error: Ref<string | null>
  fetchEditions: (eventSlug: string) => Promise<void>
  fetchEdition: (eventSlug: string, editionSlug: string) => Promise<void>
  createEdition: (eventSlug: string, payload: StoreEditionPayload) => Promise<Edition>
  updateEdition: (
    eventSlug: string,
    editionSlug: string,
    payload: UpdateEditionPayload,
  ) => Promise<Edition>
  activateEdition: (eventSlug: string, editionSlug: string) => Promise<Edition>
  deleteEdition: (eventSlug: string, editionSlug: string) => Promise<void>
} {
  return {
    editions,
    edition,
    loading,
    error,
    fetchEditions,
    fetchEdition,
    createEdition,
    updateEdition,
    activateEdition,
    deleteEdition,
  }
}
